import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal";
import { apiGet, apiPatch } from "../lib/api";
import {
  hashFor,
  kindFromHash,
  TEMPLATE_KINDS,
  TEMPLATE_KIND_META,
  type TemplateKind,
} from "../lib/template-kinds";
import { useIsNarrow } from "../lib/useIsNarrow";
import type { DashboardPayload, QuarterPrep } from "../types/dashboard";

interface TemplateView {
  kind: TemplateKind;
  body: string;
  updated_at: string;
}

interface ListResponse {
  templates: TemplateView[];
  seeding?: boolean;
  partial?: boolean;
}

interface PatchResponse {
  template: TemplateView;
}

type State =
  | { kind: "loading" }
  | { kind: "seeding"; since: number }
  | { kind: "seed-failed" }
  | { kind: "ready"; templates: TemplateView[] }
  | { kind: "error"; message: string };

const SEED_POLL_MS = 5000;
// 5 sequential Haiku calls at ~10-15s each, with occasional retries,
// can run to ~120-150s. 180s gives genuine slow-seeds enough runway
// to land without false-failing the UI.
const SEED_TIMEOUT_MS = 180_000;

export default function Templates() {
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();
  const [state, setState] = useState<State>({ kind: "loading" });
  // Local working copies of each template's body, keyed by kind.
  // Diverges from the server copy as Colleen types; reset to server
  // copy on save / refresh.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [selectedKind, setSelectedKind] = useState<TemplateKind>(() => {
    const fromHash = typeof window !== "undefined" ? kindFromHash(window.location.hash) : null;
    return fromHash ?? TEMPLATE_KINDS[0];
  });
  // Mobile-only picker for the template selector. Anchored at the
  // top of the modal; tapping the chip reveals the full list,
  // tapping a row collapses it and switches the editor below.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; message: string } | null>(null);
  const pollTimer = useRef<number | null>(null);
  // Contextual reminder for the quarterly-availability template: when
  // the quarter deadline is within 14 days, badge it in the list. Best
  // effort — reads the dashboard's quarterPrep; a failure just leaves
  // the badge off.
  const [deadlineLabel, setDeadlineLabel] = useState<string | null>(null);

  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const load = useCallback(async (): Promise<
    "ready" | "seeding" | "partial" | "empty"
  > => {
    try {
      const res = await apiGet<ListResponse>("/parish/templates");
      if (res.templates.length > 0) {
        // Any templates at all → the library is usable. A list shorter
        // than TEMPLATE_KINDS (a newly added kind still seeding, or a
        // kind the DB doesn't accept yet) renders what exists and marks
        // the rest "still drafting" — it must never block the view.
        // Merge drafts rather than overwrite: while a partial list
        // keeps polling for stragglers, an in-progress edit in the
        // textarea has to survive each refresh.
        setDrafts((prev) => {
          const next: Record<string, string> = {};
          for (const t of res.templates) next[t.kind] = prev[t.kind] ?? t.body;
          return next;
        });
        setState({ kind: "ready", templates: res.templates });
        if (res.templates.length === TEMPLATE_KINDS.length) return "ready";
        return res.seeding ? "partial" : "ready";
      }
      if (res.seeding) {
        return "seeding";
      }
      return "empty";
    } catch (err) {
      // A poll blip must not blank out an already-usable library.
      setState((s) =>
        s.kind === "ready"
          ? s
          : { kind: "error", message: (err as Error).message },
      );
      return "ready"; // not really, but stops the caller from polling
    }
  }, []);

  // Initial fetch + seeding poll loop.
  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    const tick = async () => {
      if (cancelled) return;
      const outcome = await load();
      if (cancelled) return;
      if (outcome === "seeding" || outcome === "partial") {
        if (Date.now() - start >= SEED_TIMEOUT_MS) {
          // Timing out with nothing on screen is a failure; timing
          // out with a partial list just stops the polling — the
          // usable subset stays up and the missing kind reads
          // "still drafting" until a later visit fills it.
          if (outcome === "seeding") setState({ kind: "seed-failed" });
          return;
        }
        if (outcome === "seeding") setState({ kind: "seeding", since: start });
        pollTimer.current = window.setTimeout(tick, SEED_POLL_MS);
      } else if (outcome === "empty") {
        // Empty + no seeding flag means we hit a parish_not_found
        // or RLS denied. Treat as error to stop the loop.
        setState({ kind: "error", message: "No templates configured for this parish." });
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (pollTimer.current !== null) {
        window.clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [load]);

  // Fetch the quarter deadline once (best effort, silent on failure).
  useEffect(() => {
    let cancelled = false;
    apiGet<DashboardPayload>("/dashboard")
      .then((d) => {
        if (!cancelled) setDeadlineLabel(deadlineLabelFor(d.quarterPrep));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-dismiss toast.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  // If the selected kind has no server row (deep-link hash to a kind
  // that's still drafting, or a partial list), fall back to the first
  // kind that does — otherwise the editor pane renders empty.
  useEffect(() => {
    if (state.kind !== "ready") return;
    if (state.templates.some((t) => t.kind === selectedKind)) return;
    const first = TEMPLATE_KINDS.find((k) =>
      state.templates.some((t) => t.kind === k),
    );
    if (first) setSelectedKind(first);
  }, [state, selectedKind]);

  // When the hash changes (in-app navigation, browser back, etc),
  // reflect it in the selection.
  useEffect(() => {
    const onHash = () => {
      const k = kindFromHash(window.location.hash);
      if (k) setSelectedKind(k);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Resolved data for the selected template (server copy + local
  // draft + dirty flag). Memoized so the JSX stays clean.
  const selected = useMemo(() => {
    if (state.kind !== "ready") return null;
    const serverRow = state.templates.find((t) => t.kind === selectedKind);
    if (!serverRow) return null;
    const draft = drafts[selectedKind] ?? serverRow.body;
    return {
      kind: selectedKind,
      serverBody: serverRow.body,
      draft,
      dirty: draft !== serverRow.body,
      updated_at: serverRow.updated_at,
    };
  }, [state, selectedKind, drafts]);

  const onDraftChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDrafts((prev) => ({ ...prev, [selectedKind]: e.target.value }));
  };

  const onSelectKind = (kind: TemplateKind) => {
    if (kind === selectedKind) {
      // Re-tapping the active row in the mobile picker just closes the menu.
      setPickerOpen(false);
      return;
    }
    if (selected?.dirty) {
      const ok = window.confirm("You have unsaved changes. Discard?");
      if (!ok) return;
      // Discard local edits for the previous selection.
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[selectedKind];
        return next;
      });
    }
    setSelectedKind(kind);
    setPickerOpen(false);
    if (typeof window !== "undefined") {
      history.replaceState(null, "", `${window.location.pathname}#${hashFor(kind)}`);
    }
  };

  const save = async () => {
    if (!selected || !selected.dirty || saving) return;
    setSaving(true);
    try {
      const res = await apiPatch<PatchResponse>(
        `/parish/templates/${selectedKind}`,
        { body: selected.draft },
      );
      setState((s) =>
        s.kind === "ready"
          ? {
              ...s,
              templates: s.templates.map((t) =>
                t.kind === selectedKind ? res.template : t,
              ),
            }
          : s,
      );
      setDrafts((prev) => ({ ...prev, [selectedKind]: res.template.body }));
      setToast({ kind: "ok", message: "Saved" });
    } catch (err) {
      setToast({
        kind: "err",
        message: (err as Error).message || "Couldn't save changes",
      });
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(selected.draft);
      setToast({ kind: "ok", message: "Copied to clipboard" });
    } catch {
      setToast({ kind: "err", message: "Couldn't copy to clipboard" });
    }
  };

  // Re-trigger the auto-seed safety net by re-polling. The worker
  // re-kicks the background seeder whenever it sees a parish with
  // zero rows on a /api/parish/templates GET.
  const retrySeed = () => {
    setState({ kind: "seeding", since: Date.now() });
    void load();
  };

  // ---------- Render ----------

  // Single instance of the editor body — rendered either in the
  // detail column (desktop) or inline beneath the active row (mobile
  // accordion). Centralizing here keeps the textarea + save state
  // in one DOM node so we don't fight controlled-input duplication.
  const renderEditor = (idSuffix: string): ReactNode => {
    if (!selected) return null;
    return (
      <>
        <label className="templates__label" htmlFor={`template-body-${idSuffix}`}>
          Body
        </label>
        <textarea
          id={`template-body-${idSuffix}`}
          className="templates__textarea"
          value={selected.draft}
          onChange={onDraftChange}
          rows={20}
          spellCheck
        />

        <div className="templates__actions">
          <button
            type="button"
            className="btn"
            onClick={save}
            disabled={!selected.dirty || saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button type="button" className="btn btn--ghost" onClick={copy}>
            Copy to clipboard
          </button>
        </div>

        <p className="templates__updated">
          Last updated: {relativeTime(selected.updated_at)}
        </p>
      </>
    );
  };

  const body = (() => {
    if (state.kind === "loading") {
      return <p className="placeholder">Loading templates…</p>;
    }

    if (state.kind === "seeding") {
      return (
        <div className="templates__seeding">
          <p>Drafting your starter templates. This takes about a minute.</p>
          <p className="placeholder">We'll bring you in as soon as they're ready.</p>
        </div>
      );
    }

    if (state.kind === "seed-failed") {
      return (
        <div className="templates__seeding">
          <p>
            Seeding didn't complete in time. This is usually a transient AI-API
            hiccup.
          </p>
          <button type="button" className="btn" onClick={retrySeed}>
            Try again
          </button>
        </div>
      );
    }

    if (state.kind === "error") {
      return <p className="placeholder">Couldn't load templates: {state.message}</p>;
    }

    const dirtyByKind = (kind: TemplateKind): boolean =>
      state.kind === "ready" &&
      drafts[kind] !== undefined &&
      drafts[kind] !== state.templates.find((t) => t.kind === kind)?.body;

    // A kind with no server row yet (partial seed) is listed but not
    // selectable — its row says it's still being drafted.
    const availableByKind = (kind: TemplateKind): boolean =>
      state.kind === "ready" && state.templates.some((t) => t.kind === kind);
    const DRAFTING_BLURB = "Still being drafted — check back shortly.";

    if (isNarrow) {
      // Mobile: picker chip at the top, editor anchored beneath it.
      // Tapping the chip drops a popover menu of all templates so
      // switching is one tap without scrolling.
      const activeMeta = selected ? TEMPLATE_KIND_META[selected.kind] : null;
      return (
        <div className="templates__layout templates__layout--mobile">
          <div className="templates__picker">
            <button
              type="button"
              className="templates__picker-trigger"
              onClick={() => setPickerOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
            >
              <span className="templates__picker-trigger-label">
                {activeMeta?.label ?? "Select template"}
                {selected && dirtyByKind(selected.kind) ? (
                  <span
                    className="templates__sidebar-dirty"
                    aria-label="unsaved changes"
                    title="Unsaved changes"
                  >
                    •
                  </span>
                ) : null}
              </span>
              <span
                className={`templates__picker-chevron${pickerOpen ? " is-open" : ""}`}
                aria-hidden="true"
              >
                ▾
              </span>
            </button>

            {pickerOpen ? (
              <ul className="templates__picker-menu" role="listbox">
                {TEMPLATE_KINDS.map((kind) => {
                  const meta = TEMPLATE_KIND_META[kind];
                  const isActive = kind === selectedKind;
                  const available = availableByKind(kind);
                  return (
                    <li key={kind} role="option" aria-selected={isActive}>
                      <button
                        type="button"
                        className={`templates__picker-option${isActive ? " is-active" : ""}${available ? "" : " is-unavailable"}`}
                        onClick={() => onSelectKind(kind)}
                        disabled={!available}
                      >
                        <span className="templates__picker-option-check" aria-hidden="true">
                          {isActive ? "✓" : ""}
                        </span>
                        <span className="templates__picker-option-text">
                          <span className="templates__picker-option-label">
                            {meta.label}
                            {dirtyByKind(kind) ? (
                              <span
                                className="templates__sidebar-dirty"
                                aria-label="unsaved changes"
                              >
                                •
                              </span>
                            ) : null}
                          </span>
                          {kind === "quarterly_availability" && deadlineLabel ? (
                            <span className="templates__sidebar-deadline">{deadlineLabel}</span>
                          ) : null}
                          <span className="templates__picker-option-blurb">
                            {available ? meta.blurb : DRAFTING_BLURB}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>

          {selected && activeMeta ? (
            <section
              className="templates__detail templates__detail--mobile"
              aria-labelledby="templates-detail-title"
            >
              <p className="templates__detail-blurb">{activeMeta.blurb}</p>
              {renderEditor("mobile")}
            </section>
          ) : null}
        </div>
      );
    }

    // Desktop: sidebar (list) + detail (editor) side-by-side.
    return (
      <div className="templates__layout">
        <nav className="templates__sidebar" aria-label="Template list">
          <ul>
            {TEMPLATE_KINDS.map((kind) => {
              const meta = TEMPLATE_KIND_META[kind];
              const isActive = kind === selectedKind;
              const available = availableByKind(kind);
              return (
                <li key={kind}>
                  <button
                    type="button"
                    className={`templates__sidebar-item${isActive ? " is-active" : ""}${available ? "" : " is-unavailable"}`}
                    onClick={() => onSelectKind(kind)}
                    disabled={!available}
                    aria-current={isActive ? "true" : undefined}
                  >
                    <span className="templates__sidebar-label">
                      {meta.label}
                      {dirtyByKind(kind) ? (
                        <span
                          className="templates__sidebar-dirty"
                          aria-label="unsaved changes"
                          title="Unsaved changes"
                        >
                          •
                        </span>
                      ) : null}
                    </span>
                    {kind === "quarterly_availability" && deadlineLabel ? (
                      <span className="templates__sidebar-deadline">{deadlineLabel}</span>
                    ) : null}
                    <span className="templates__sidebar-blurb">
                      {available ? meta.blurb : DRAFTING_BLURB}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {selected ? (
          <section className="templates__detail" aria-labelledby="templates-detail-title">
            <h2 id="templates-detail-title" className="templates__detail-title">
              {TEMPLATE_KIND_META[selected.kind].label}
            </h2>
            <p className="templates__detail-blurb">
              {TEMPLATE_KIND_META[selected.kind].blurb}
            </p>

            {renderEditor("detail")}
          </section>
        ) : null}
      </div>
    );
  })();

  return (
    <Modal open onClose={close} ariaLabel="Email templates">
      <header className="templates-modal__header">
        <h2 className="templates-modal__title">Email templates</h2>
      </header>
      {body}
      {toast ? (
        <div
          className={`toast toast--${toast.kind}`}
          role={toast.kind === "err" ? "alert" : "status"}
        >
          {toast.message}
        </div>
      ) : null}
    </Modal>
  );
}

// "Deadline in 7 days" for the quarterly-availability template, only
// when the quarter deadline is within the next two weeks (matches the
// dashboard's ≤14-day quarter-prep window). Null → no badge.
function deadlineLabelFor(quarterPrep: QuarterPrep | null): string | null {
  if (!quarterPrep) return null;
  const days = quarterPrep.daysRemaining;
  if (days < 0 || days > 14) return null;
  if (days === 0) return "Deadline today";
  if (days === 1) return "Deadline in 1 day";
  return `Deadline in ${days} days`;
}

function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const diff = Date.now() - then;
  if (diff < 60_000) return "just now";
  const minutes = Math.round(diff / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  return new Date(then).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
